/* Poufudic - customer story player.
 *
 * Drives every [data-pf-story] button on the page, wherever it lives: the cart
 * page rail and the cart-drawer rail share one player instance, because the
 * drawer is re-rendered over AJAX and a per-rail player would be destroyed with
 * it. Listening on the document means a rail that appears later just works.
 *
 * THE LOADING CONTRACT
 * Nothing here touches the network until a circle is tapped. On open we set the
 * poster and exactly one video src. While that story plays we prefetch ONLY the
 * next one, and we cancel that prefetch on close. A shopper who opens nothing
 * pays nothing; one who opens three stories pays for three.
 *
 * WHY A NATIVE <dialog>
 * showModal() gives us focus trapping, Escape-to-close, background inert-ness
 * and focus restoration for free, all of which are easy to hand-roll badly.
 *
 * WCAG, load-bearing:
 *   1.4.2  starts muted, visible unmute control
 *   2.2.2  auto-advance to the next story has a pause control
 *   2.1.1  arrow keys and space work, not just taps
 */
(function () {
  'use strict';

  if (window.__pfStoryPlayerLoaded__) { return; }
  window.__pfStoryPlayerLoaded__ = true;

  var SMALL_SCREEN = '(max-width: 430px)';
  var HOLD_MS = 220;      // press longer than this and it is a hold, not a tap
  var SWIPE_CLOSE_PX = 90;

  var dlg, stage, video, blur, barsWrap, capEl, soundBtn, pauseBtn;
  var stories = [];
  var index = -1;
  var prefetchLink = null;
  var holdTimer = null;
  var didHold = false;
  var touchStartY = 0;

  /* ---------- collect ---------- */

  function readRails() {
    stories = [];
    var btns = document.querySelectorAll('[data-pf-story]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (!b.dataset.mp4) { continue; }
      stories.push({
        btn: b,
        mp4: b.dataset.mp4,
        mp4Sm: b.dataset.mp4Sm || b.dataset.mp4,
        poster: b.dataset.poster || '',
        caption: b.dataset.caption || ''
      });
    }
    return stories.length;
  }

  function srcFor(s) {
    return window.matchMedia(SMALL_SCREEN).matches ? s.mp4Sm : s.mp4;
  }

  /* ---------- prefetch the NEXT story only ---------- */

  function clearPrefetch() {
    if (prefetchLink && prefetchLink.parentNode) {
      prefetchLink.parentNode.removeChild(prefetchLink);
    }
    prefetchLink = null;
  }

  function prefetchNext() {
    clearPrefetch();
    var n = stories[index + 1];
    if (!n) { return; }
    // Save-Data and 2g mean the shopper is paying for bytes or waiting for them.
    var c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) { return; }
    prefetchLink = document.createElement('link');
    prefetchLink.rel = 'prefetch';
    prefetchLink.as = 'video';
    prefetchLink.href = srcFor(n);
    document.head.appendChild(prefetchLink);
  }

  /* ---------- progress bars ---------- */

  function buildBars() {
    barsWrap.innerHTML = '';
    for (var i = 0; i < stories.length; i++) {
      var b = document.createElement('span');
      b.className = 'pfsp__bar';
      var f = document.createElement('i');
      f.className = 'pfsp__barfill';
      b.appendChild(f);
      barsWrap.appendChild(b);
    }
  }

  function paintBars() {
    var bars = barsWrap.children;
    for (var i = 0; i < bars.length; i++) {
      var fill = bars[i].firstChild;
      fill.style.width = i < index ? '100%' : (i > index ? '0%' : fill.style.width || '0%');
    }
  }

  function onTime() {
    var bar = barsWrap.children[index];
    if (!bar || !video.duration) { return; }
    var pct = Math.min(100, (video.currentTime / video.duration) * 100);
    bar.firstChild.style.width = pct.toFixed(2) + '%';
  }

  /* ---------- open / advance / close ---------- */

  function show(i) {
    if (i < 0) { i = 0; }
    if (i >= stories.length) { close(); return; }
    index = i;
    var s = stories[i];

    video.pause();
    video.removeAttribute('src');
    if (s.poster) {
      video.poster = s.poster;
      blur.style.backgroundImage = 'url("' + s.poster + '")';
    }
    video.src = srcFor(s);
    video.currentTime = 0;
    capEl.textContent = s.caption;
    dlg.setAttribute('aria-label', s.caption ? 'Customer video: ' + s.caption : 'Customer video');

    paintBars();
    setPaused(false);

    var p = video.play();
    if (p && p.catch) {
      // Autoplay can still be refused; the poster stays up and the shopper can
      // press play. Never let a rejected promise surface as an unhandled error.
      p.catch(function () { setPaused(true); });
    }
    prefetchNext();
  }

  function next() { show(index + 1); }
  function prev() { show(index - 1); }

  function open(i) {
    if (!readRails()) { return; }
    buildBars();
    if (!dlg.open) { dlg.showModal(); }
    document.documentElement.classList.add('pfsp-open');
    show(i);
  }

  function close() {
    video.pause();
    video.removeAttribute('src');
    video.load();
    clearPrefetch();
    document.documentElement.classList.remove('pfsp-open');
    if (dlg.open) { dlg.close(); }
    index = -1;
  }

  /* ---------- controls ---------- */

  function setPaused(paused) {
    if (paused) { video.pause(); } else { video.play().catch(function () {}); }
    dlg.classList.toggle('is-paused', paused);
    if (pauseBtn) {
      pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      pauseBtn.setAttribute('aria-label', paused ? 'Resume' : 'Pause');
    }
  }

  function setMuted(muted) {
    video.muted = muted;
    dlg.classList.toggle('is-muted', muted);
    if (soundBtn) {
      soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
      soundBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    }
  }

  /* ---------- wire ---------- */

  function init() {
    dlg = document.querySelector('[data-pf-story-player]');
    if (!dlg) { return; }
    stage = dlg.querySelector('[data-pfsp-stage]');
    video = dlg.querySelector('[data-pfsp-video]');
    blur = dlg.querySelector('[data-pfsp-blur]');
    barsWrap = dlg.querySelector('[data-pfsp-bars]');
    capEl = dlg.querySelector('[data-pfsp-caption]');
    soundBtn = dlg.querySelector('[data-pfsp-sound]');
    pauseBtn = dlg.querySelector('[data-pfsp-pause]');
    if (!video || !stage) { return; }

    video.muted = true;
    setMuted(true);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-pf-story]');
      if (!btn) { return; }
      e.preventDefault();
      readRails();
      for (var i = 0; i < stories.length; i++) {
        if (stories[i].btn === btn) { open(i); return; }
      }
    });

    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', next);

    dlg.addEventListener('close', function () {
      video.pause();
      clearPrefetch();
      document.documentElement.classList.remove('pfsp-open');
    });

    // Clicking the backdrop closes. A native dialog reports backdrop clicks as
    // clicks on the dialog element itself, so compare against the stage.
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) { close(); }
    });

    var closeBtn = dlg.querySelector('[data-pfsp-close]');
    if (closeBtn) { closeBtn.addEventListener('click', close); }
    if (soundBtn) { soundBtn.addEventListener('click', function () { setMuted(!video.muted); }); }
    if (pauseBtn) { pauseBtn.addEventListener('click', function () { setPaused(!video.paused); }); }

    var prevBtn = dlg.querySelector('[data-pfsp-prev]');
    var nextBtn = dlg.querySelector('[data-pfsp-next]');
    if (prevBtn) { prevBtn.addEventListener('click', prev); }
    if (nextBtn) { nextBtn.addEventListener('click', next); }

    // Press and hold pauses; a short press advances. Pointer events cover mouse
    // and touch in one path.
    stage.addEventListener('pointerdown', function (e) {
      if (e.target.closest('button, a')) { return; }
      didHold = false;
      touchStartY = e.clientY;
      holdTimer = setTimeout(function () { didHold = true; setPaused(true); }, HOLD_MS);
    });

    stage.addEventListener('pointerup', function (e) {
      if (e.target.closest('button, a')) { return; }
      clearTimeout(holdTimer);
      if (didHold) { setPaused(false); didHold = false; return; }
      if (e.clientY - touchStartY > SWIPE_CLOSE_PX) { close(); return; }
      var r = stage.getBoundingClientRect();
      if (e.clientX - r.left < r.width * 0.3) { prev(); } else { next(); }
    });

    stage.addEventListener('pointercancel', function () {
      clearTimeout(holdTimer);
      if (didHold) { setPaused(false); didHold = false; }
    });

    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === ' ' && !e.target.closest('button, a')) {
        e.preventDefault();
        setPaused(!video.paused);
      }
    });

    // A viewport crossing 430px mid-story should not restart it, but the next
    // story should pick the right file.
    if (window.matchMedia) {
      window.matchMedia(SMALL_SCREEN).addEventListener('change', prefetchNext);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () { readRails(); });
})();
